import { LINKEDIN_PLAN_LEGS_WIRED, type PlanProvider } from './types';

import { PAID_MEDIA_PROVIDER_LABELS, type PaidMediaProvider } from '@/lib/paid-media/providers';

export interface ConceptIntent {
  intent: string;
  platform: string | null;
}

export interface OmittedChannel {
  provider: PaidMediaProvider | null;
  name?: string;
  reason: string;
}

export interface ChannelIntent {
  named: PaidMediaProvider[];
  unrecognised: string[];
  anyChannel: boolean;
}

export interface PlanChannels {
  providers: PlanProvider[];
  omitted: OmittedChannel[];
  named: PaidMediaProvider[];
  basis: 'concept' | 'connected';
}

interface IntentCarrier {
  id: string;
  status: string;
  intents: readonly ConceptIntent[];
}

export interface IntentWorkspace {
  personas: readonly { concepts: readonly IntentCarrier[] }[];
  unassignedConcepts: readonly IntentCarrier[];
}

export const AD_PLATFORM_PROVIDER: Readonly<Record<string, PaidMediaProvider>> = {
  meta: 'meta-ads',
  google_rsa: 'google-ads',
  linkedin: 'linkedin-ads',
};

const PLATFORM_PROVIDER: Readonly<Record<string, PaidMediaProvider>> = {
  ...AD_PLATFORM_PROVIDER,
  'meta-ads': 'meta-ads',
  facebook: 'meta-ads',
  instagram: 'meta-ads',
  google: 'google-ads',
  'google-ads': 'google-ads',
  linkedin: 'linkedin-ads',
  'linkedin-ads': 'linkedin-ads',
};

const PROVIDER_ORDER: readonly PaidMediaProvider[] = ['meta-ads', 'google-ads', 'linkedin-ads'];

export function paidProviderOfPlatform(
  platform: string | null | undefined,
): PaidMediaProvider | null {
  return PLATFORM_PROVIDER[(platform ?? '').trim().toLowerCase()] ?? null;
}

export function namedPaidProviders(intents: readonly ConceptIntent[]): PaidMediaProvider[] {
  const found = new Set<PaidMediaProvider>();
  for (const entry of intents) {
    if (entry.intent !== 'paid') continue;
    const provider = paidProviderOfPlatform(entry.platform);
    if (provider) found.add(provider);
  }
  return PROVIDER_ORDER.filter((provider) => found.has(provider));
}

const PLATFORM_NAMES: Readonly<Record<string, string>> = {
  tiktok: 'TikTok',
  twitter: 'X',
  x: 'X',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  snapchat: 'Snapchat',
  reddit: 'Reddit',
};

function platformName(platform: string): string {
  const key = platform.trim().toLowerCase();
  return PLATFORM_NAMES[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

export function readChannelIntent(intents: readonly ConceptIntent[]): ChannelIntent {
  const unrecognised = new Set<string>();
  let anyChannel = false;
  for (const entry of intents) {
    if (entry.intent !== 'paid') continue;
    const platform = (entry.platform ?? '').trim();
    if (!platform) anyChannel = true;
    else if (!paidProviderOfPlatform(platform)) unrecognised.add(platformName(platform));
  }
  return { named: namedPaidProviders(intents), unrecognised: [...unrecognised], anyChannel };
}

function nameOf(provider: PaidMediaProvider): string {
  return PAID_MEDIA_PROVIDER_LABELS[provider];
}

function joinedNames(names: readonly string[]): string {
  return names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function joined(providers: readonly PaidMediaProvider[]): string {
  return joinedNames(providers.map(nameOf));
}

export function planChannelsFor(input: {
  named: readonly PaidMediaProvider[];
  unrecognised?: readonly string[];
  anyChannel?: boolean;
  connected: readonly PlanProvider[];
}): PlanChannels {
  const named = PROVIDER_ORDER.filter((provider) => input.named.includes(provider));
  const unrecognised = input.unrecognised ?? [];
  const connected = PROVIDER_ORDER.filter(
    (provider): provider is PlanProvider =>
      provider !== 'linkedin-ads' && input.connected.includes(provider),
  );
  if (input.anyChannel || (named.length === 0 && unrecognised.length === 0)) {
    return { providers: connected, omitted: [], named, basis: 'connected' };
  }
  // LinkedIn joins a plan only when a Concept names it and it is connected; it is never added just
  // because an account is connected, since a store's product is not a LinkedIn campaign by default.
  const linkedIn =
    LINKEDIN_PLAN_LEGS_WIRED &&
    named.includes('linkedin-ads') &&
    input.connected.includes('linkedin-ads');
  const providers: PlanProvider[] = [
    ...connected.filter((provider) => named.includes(provider)),
    ...(linkedIn ? (['linkedin-ads'] as const) : []),
  ];
  const setUpFor = named.length > 0 ? joined(named) : joinedNames(unrecognised);
  const omitted: OmittedChannel[] = [
    ...connected
      .filter((provider) => !named.includes(provider))
      .map((provider) => ({
        provider,
        reason: `Your concept is set up for ${setUpFor}, so no ${nameOf(provider)} campaign was prepared.`,
      })),
    ...named
      .filter((provider) => !providers.includes(provider as PlanProvider))
      .map((provider) => ({
        provider,
        reason:
          provider === 'linkedin-ads'
            ? LINKEDIN_PLAN_LEGS_WIRED
              ? 'Your concept names LinkedIn, but no LinkedIn Ads account is connected.'
              : 'Your concept names LinkedIn. LinkedIn campaigns are not prepared on this page.'
            : `Your concept names ${nameOf(provider)}, but no ${nameOf(provider)} account is connected.`,
      })),
    ...unrecognised.map((name) => ({
      provider: null,
      name,
      reason: `Your concept names ${name}. ${name} campaigns are not prepared on this page.`,
    })),
  ];
  return { providers, omitted, named, basis: 'concept' };
}

export function providersLeftOut(notUsed: readonly { what: string }[]): PaidMediaProvider[] {
  return PROVIDER_ORDER.filter((provider) =>
    notUsed.some((entry) => entry.what === `${nameOf(provider)} campaign`),
  );
}

export function omittedAsNotUsed(
  omitted: readonly OmittedChannel[],
): { what: string; why: string }[] {
  return omitted.map((entry) => ({
    what: `${entry.provider ? nameOf(entry.provider) : (entry.name ?? 'Other channel')} campaign`,
    why: entry.reason,
  }));
}

export function conceptIntentsOf(
  workspace: IntentWorkspace,
  conceptIds: string | readonly string[] | null,
): ConceptIntent[] {
  const all = [
    ...workspace.personas.flatMap((persona) => persona.concepts),
    ...workspace.unassignedConcepts,
  ];
  const wanted = typeof conceptIds === 'string' ? [conceptIds] : conceptIds;
  if (wanted && wanted.length > 0) {
    return all
      .filter((concept) => wanted.includes(concept.id))
      .flatMap((concept) => [...concept.intents]);
  }
  return all
    .filter((concept) => concept.status !== 'archived')
    .flatMap((concept) => [...concept.intents]);
}

export interface ChannelCandidate {
  id: string;
  intents: readonly ConceptIntent[];
  /** The `ad_platform` of each unit of this Concept's copy that the person approved. */
  approvedPlatforms: readonly string[];
}

/**
 * The one Concept each channel's ad is written from when several Concepts share one plan. A Concept is a
 * candidate for a channel when it names the channel, or names none (every connected channel). Among the
 * candidates, a Concept with approved copy for that channel is preferred, then the Concepts' own order
 * decides. A channel no Concept can serve is left out, and its ad is then written from the whole Brief.
 */
export function conceptForEachChannel(
  candidates: readonly ChannelCandidate[],
  providers: readonly PlanProvider[],
): Partial<Record<PlanProvider, string>> {
  const out: Partial<Record<PlanProvider, string>> = {};
  for (const provider of providers) {
    const eligible = candidates.filter((candidate) => {
      const intent = readChannelIntent(candidate.intents);
      const open =
        intent.anyChannel || (intent.named.length === 0 && intent.unrecognised.length === 0);
      return open || intent.named.includes(provider);
    });
    const withCopy = eligible.filter((candidate) =>
      candidate.approvedPlatforms.some((platform) => AD_PLATFORM_PROVIDER[platform] === provider),
    );
    const naming = eligible.filter((candidate) =>
      readChannelIntent(candidate.intents).named.includes(provider),
    );
    const chosen = withCopy[0] ?? naming[0] ?? eligible[0];
    if (chosen) out[provider] = chosen.id;
  }
  return out;
}
